import { useTranslation } from 'react-i18next';
import Button from '../../../components/base/Button';
import WindowsMt5DownloadModal from '../../../components/download/WindowsMt5DownloadModal';
import { useWindowsMt5Download } from '../../../hooks/useWindowsMt5Download';
import { getAndroidDownloadRouteUrl } from '../../../utils/androidDownload';
import { WINDOWS_MT5_OFFICIAL_DOWNLOAD_LINK } from '../../../utils/windowsDownload';

export default function DownloadSection() {
  const { t } = useTranslation();
  const androidDownloadLink = getAndroidDownloadRouteUrl();
  const {
    showWindowsMt5Modal,
    checkingWindowsMt5Region,
    closeWindowsMt5Modal,
    handleWindowsMt5DownloadClick,
  } = useWindowsMt5Download();

  const platforms = [
    {
      name: 'Windows',
      icon: 'ri-windows-line',
      description: t('platform.ffd'),
      version: t('download.version', { version: '5.0.3950', defaultValue: 'Version {{version}}' }),
      size: '45.2 MB',
      link : WINDOWS_MT5_OFFICIAL_DOWNLOAD_LINK,
      color: 'bg-blue-500',
      isWindows: true
    },
    {
      name: 'macOS',
      icon: 'ri-apple-line',
      description: t('platform.nma'),
      version: t('download.version', { version: '5.0.3950', defaultValue: 'Version {{version}}' }),
      size: '52.1 MB',
      link : 'https://download.mql5.com/cdn/web/metaquotes.ltd/mt5/MetaTrader5.pkg.zip?utm_source=support.metaquotes.net&utm_campaign=download.mt5.macos',
      color: 'bg-gray-700',
      isWindows: false
    },
    {
      name: 'Android',
      icon: 'ri-android-line',
      description: t('platform.mta'),
      version: t('download.version', { version: '5.0.3950', defaultValue: 'Version {{version}}' }),
      size: '28.5 MB',
      link : androidDownloadLink,
      color: 'bg-green-500',
      isWindows: false
    },
    {
      name: 'iOS',
      icon: 'ri-apple-line',
      description: t('platform.iai'),
      version: t('download.version', { version: '5.0.3950', defaultValue: 'Version {{version}}' }),
      size: '31.2 MB',
      link : 'https://apps.apple.com/us/app/metatrader-5/id413251709',
      color: 'bg-blue-600',
      isWindows: false
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
           {t('platform.dmt')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
           {t('platform.ayt')}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {platforms.map((platform, index) => (
            <div 
              key={index}
              className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
            >
              <div className="flex items-center mb-4">
                <div className={`w-12 h-12 ${platform.color} rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${platform.icon} text-2xl text-white`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {platform.name}
                  </h3>
                  <p className="text-sm text-gray-500">{platform.version}</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-4 leading-relaxed">
                {platform.description}
              </p>
              
              <div className="flex justify-between items-center mb-6">
                <span className="text-sm text-gray-500">{t('download.size', { size: platform.size, defaultValue: 'Size: {{size}}' })}</span>
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
                  {t('platform.free')}
                </span>
              </div>
              
              {platform.isWindows ? (
                <Button
                  type="button"
                  onClick={handleWindowsMt5DownloadClick}
                  disabled={checkingWindowsMt5Region}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <i className="ri-download-line mr-2"></i>
                  {checkingWindowsMt5Region ? t("download.checkingRegion", { defaultValue: "識別中..." }) : t('platform.dn')}
                </Button>
              ) : (
                <a href={platform.link}>
                  <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 whitespace-nowrap">
                    <i className="ri-download-line mr-2"></i>
                    {t('platform.dn')}
                  </Button>
                </a>
              )}
            </div>
          ))}
        </div>
        
        {/* System Requirements */}
        <div className="bg-gray-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
           {t('platform.sr')}
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <i className="ri-computer-line text-3xl text-amber-600 mb-3"></i>
              <h4 className="font-bold text-gray-900 mb-2">Windows</h4>
              <p className="text-sm text-gray-600">{t("download.windowsRequirements", { defaultValue: "Windows 10/11, 2GB RAM, 1GB Storage" })}</p>
            </div>
            <div className="text-center">
              <i className="ri-apple-line text-3xl text-amber-600 mb-3"></i>
              <h4 className="font-bold text-gray-900 mb-2">macOS</h4>
              <p className="text-sm text-gray-600">{t("download.macRequirements", { defaultValue: "macOS 10.15+, 4GB RAM, 1GB Storage" })}</p>
            </div>
            <div className="text-center">
              <i className="ri-smartphone-line text-3xl text-amber-600 mb-3"></i>
              <h4 className="font-bold text-gray-900 mb-2">{t("download.mobile", { defaultValue: "Mobile" })}</h4>
              <p className="text-sm text-gray-600">Android 6.0+ / iOS 12.0+</p>
            </div>
            <div className="text-center">
              <i className="ri-wifi-line text-3xl text-amber-600 mb-3"></i>
              <h4 className="font-bold text-gray-900 mb-2">{t("download.internet", { defaultValue: "Internet" })}</h4>
              <p className="text-sm text-gray-600">{t('platform.si')}</p>
            </div>
          </div>
        </div>
        
        {/* Help Section */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
           {t('platform.nh')}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('platform.os')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="border-2 border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white px-8 py-3 whitespace-nowrap">
              <i className="ri-book-line mr-2"></i>
              {t('platform.ug')}
            </Button>
            <Button className="border-2 border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white px-8 py-3 whitespace-nowrap">
              <i className="ri-customer-service-line mr-2"></i>
              {t('platform.cs')}
            </Button>
          </div>
        </div>
      </div>

      <WindowsMt5DownloadModal open={showWindowsMt5Modal} onClose={closeWindowsMt5Modal} />
    </section>
  );
}
